import type {
  CompensationsPayrollConceptFragment,
  CompensationsTimeReimbursementRowFragment,
  TimeSettingsCustomTimeRangeCategoryFragment,
} from 'generated/resources/graphql'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import MapTimeCategoryWithPayrollConcept from 'modules/compensations/components/MapTimeCategoryWithPayrollConcept'
import useUpsertReimbursablePayrollConcept from 'modules/compensations/hooks/useUpsertReimbursablePayrollConcept'

type Props = {
  reimbursements: CompensationsTimeReimbursementRowFragment[]
  payrollConcepts: CompensationsPayrollConceptFragment[]
  timeRangeCategories: TimeSettingsCustomTimeRangeCategoryFragment[]
}

const TimeReimbursements = ({ reimbursements, payrollConcepts, timeRangeCategories }: Props) => {
  const { upsert: upsertCompensationMapping } = useUpsertReimbursablePayrollConcept()

  const timeRangeCategoriesWithPayrollConcepts = timeRangeCategories.filter((category) =>
    reimbursements.some(
      (reimbursment) => reimbursment.reimbursableId === category.id && reimbursment.payrollConceptId
    )
  )

  const onRowChanged = async (timeCategoryId: string, payrollConceptId: string) => {
    const reimbursement = reimbursements.find((r) => r.reimbursableId === timeCategoryId)

    if (!reimbursement) {
      return
    }

    const result = await upsertCompensationMapping({
      id: reimbursement.id,
      payrollConceptId: payrollConceptId,
      reimbursableId: reimbursement.reimbursableId,
      reimbursableType: reimbursement.reimbursableType,
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  return (
    <MapTimeCategoryWithPayrollConcept
      reimbursements={reimbursements}
      payrollConcepts={payrollConcepts}
      timeRangeCategories={timeRangeCategoriesWithPayrollConcepts}
      onRowChanged={onRowChanged}
    />
  )
}

export default TimeReimbursements
